/**
 * admin/src/hooks/useNotifications.ts
 * Hooks cho Notifications management
 *
 * useNotifications   — lịch sử gửi email (paginated)
 * useNotifMutations  — gửi email hàng loạt
 */

import { useState, useEffect, useCallback } from 'react';
import { adminFetch } from './useAdmin';
import { toast } from '../components/ui/index';
import type {
  AdminNotificationLog,
  NotificationTarget,
  PaginatedResponse,
} from '../types/admin';

// ─── List ─────────────────────────────────────────────────────────────────────
interface UseNotificationsReturn {
  logs: AdminNotificationLog[];
  total: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useNotifications(page = 1, limit = 20): UseNotificationsReturn {
  const [data, setData] = useState<PaginatedResponse<AdminNotificationLog> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    adminFetch<PaginatedResponse<AdminNotificationLog>>('/notifications', {
      params: { page, limit },
    })
      .then((res) => { if (!cancelled) { setData(res); setIsLoading(false); } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lỗi tải lịch sử');
          setIsLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [page, limit, tick]);

  return {
    logs: data?.data ?? [],
    total: data?.total ?? 0,
    totalPages: data?.total_pages ?? 1,
    isLoading,
    error,
    refetch,
  };
}

// ─── Mutations ────────────────────────────────────────────────────────────────
interface SendPayload {
  subject: string;
  body: string;
  target: NotificationTarget;
  specific_email?: string;
  limit_send?: number;
}

interface SendResult {
  ok: boolean;
  sent_count: number;
  fail_count: number;
  total: number;
  target: string;
  failures?: string[];
}

export function useNotifMutations(onSuccess?: () => void) {
  const [isSending, setIsSending] = useState(false);
  const [lastResult, setLastResult] = useState<SendResult | null>(null);

  async function sendNotification(payload: SendPayload): Promise<SendResult | null> {
    setIsSending(true);
    setLastResult(null);
    try {
      const res = await adminFetch<SendResult>('/notifications/send', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setLastResult(res);
      toast(
        `Đã gửi ${res.sent_count}/${res.total} email${res.fail_count > 0 ? ` (${res.fail_count} lỗi)` : ''}`,
        res.fail_count === 0 ? 'success' : 'warning'
      );
      onSuccess?.();
      return res;
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Lỗi gửi email', 'error');
      return null;
    } finally {
      setIsSending(false);
    }
  }

  // URL preview HTML email (mở trong tab mới)
  function getPreviewUrl(subject: string, body: string): string {
    const API_BASE = import.meta.env.DEV ? '' : 'https://luyenthi.io.vn';
    const params = new URLSearchParams({ subject, body });
    return `${API_BASE}/api/admin/notifications/preview?${params.toString()}`;
  }

  return { sendNotification, isSending, lastResult, getPreviewUrl };
}
